/**
 * Wall feature — customer photo feed tagged to vendors.
*/

// ─── Shared image-URL resolver (same logic as vendor_product_service_listing.js) ───

import { getImageUrl } from "../helpers/mediaUrl";
import Tribute from "tributejs";

// ─── Helper: format plain-text URLs as clickable anchors ─────────────────────
const formatCaptionUrls = (text) => {
  if (!text) return "";
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(
    urlRegex,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );
};

// ─── Helper: format @mentions as clickable vendor links ─────────────────────
const formatMentions = (text, vendors = []) => {
  if (!text || vendors.length === 0) return text || "";

  // Sort vendors by length descending to match longest names first
  const sortedVendors = [...vendors].sort((a, b) => b.Vendor_Name.length - a.Vendor_Name.length);

  let formattedText = text;

  // We'll iterate through all vendors and replace their mentions.
  // Using a temporary placeholder to avoid double-processing.
  sortedVendors.forEach((v, index) => {
    const url = v.Vendor_URL
      ? `${baseURL}customer/productListing/${encodeURIComponent(v.Vendor_URL)}`
      : "#";
    
    // The link text will have spaces removed as requested (e.g. @FrozenWorld)
    const collapsedName = v.Vendor_Name.replace(/\s+/g, "");
    const displayMention = "@" + collapsedName;
    const linkHtml = `<a href="${url}" class="mention-link">${displayMention}</a>`;

    // We match both the regular name (@Frozen World) and the collapsed name (@FrozenWorld)
    const escapedRegular = v.Vendor_Name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedCollapsed = collapsedName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    
    const boundary = "(?=\\s|$|\\.|!|\\?)";
    const regexRegular = new RegExp("@" + escapedRegular + boundary, "gi");
    const regexCollapsed = new RegExp("@" + escapedCollapsed + boundary, "gi");
    
    formattedText = formattedText.replace(regexRegular, linkHtml);
    formattedText = formattedText.replace(regexCollapsed, linkHtml);
  });

  return formattedText;
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return "";

  if (window.moment && typeof window.moment === "function") {
    return window.moment(dateString).fromNow();
  }

  const dt = new Date(String(dateString).replace(" ", "T"));
  if (Number.isNaN(dt.getTime())) return dateString;

  const diffSec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (diffSec < 60) return "just now";

  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins} min ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

  return dt.toLocaleDateString();
};

// ─────────────────────────────────────────────────────────────────────────────

export default class Wall {
  constructor() {
    if (window.__locasoWallInitialized) {
      return;
    }
    window.__locasoWallInitialized = true;

    this.$wallFeed = $("#wall-feed");
    // Must match PHP: session key is CUSTOMER_ID from login (not only Customer_ID).
    this.canUpload = this.$wallFeed.attr("data-can-upload") === "1";
    this.$uploadForm = $("#uploadForm");
    this.$imageInput = $("#imageInput");
    // this.$vendorSelect = $("#vendorSelect"); // Removed in favor of mentions
    this.$vendorIdInput = $("#vendorIdInput");
    this.$previewTrigger = $("#previewTrigger");
    this.$imagePreview = $("#imagePreview");
    this.$submitBtn = $("#submitPost");
    this.isSubmitting = false;

    // Comment Modal Selectors
    this.$commentModal = $("#commentModal");
    this.$modalCommentList = $("#modal-comment-list");
    this.$modalCommentInput = $("#modal-comment-input");
    this.$modalBtnSend = $("#modal-btn-send");

    this.bindEvents();
    this.loadVendors(() => {
      this.initInfiniteScroll();
      this.loadWallPosts();
    });

    this.tribute = null;
    this.vendorsData = [];
    this.activePostId = null;

    // Pagination state
    this.offset = 0;
    this.limit = 12; // Load 12 posts at a time
    this.isLoading = false;
    this.hasMore = true;
    this.$loadMoreIndicator = $("#wall-load-more-indicator");
  }

  // ─── Event binding ────────────────────────────────────────────────────────

  bindEvents = () => {
    // Remove any previous handlers to prevent duplicate binding
    this.$previewTrigger.off(".wall");
    this.$imageInput.off(".wall");
    this.$uploadForm.off(".wall");
    this.$wallFeed.off(".wall");
    $(document).off(".wall");

    // 1. Photo triggers (using labels now, but we still need preview logic)
    // 2. Live image preview
    this.$imageInput.on("change.wall", (e) => this.handleImagePreview(e));
    $("#cameraInput").on("change.wall", (e) => {
      if (e.target.files.length) {
        // Synchronize files so the main form submit works correctly
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(e.target.files[0]);
        this.$imageInput[0].files = dataTransfer.files;
        this.handleImagePreview(e);
      }
    });

    // Form submit
    this.$uploadForm.on("submit.wall", this.handleUploadSubmit);





    // Interaction: Like
    this.$wallFeed.on("click.wall", ".like-btn", (e) => {
      const $btn = $(e.currentTarget);
      const postId = $btn.data("id");
      this.handleLikeToggle($btn, postId);
    });

    // Interaction: Comment toggle (Opens Modal)
    this.$wallFeed.on("click.wall", ".comment-toggle-btn", (e) => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      this.handleCommentModalOpen($btn);
    });

    // Modal: Submit Comment
    this.$modalBtnSend.on("click.wall", () => {
      const text = this.$modalCommentInput.val().trim();
      if (text && this.activePostId) {
        this.handleCommentSubmit(this.$modalBtnSend, this.activePostId, text, this.$modalCommentInput);
      }
    });

    // Modal: Enter key to submit
    this.$modalCommentInput.on("keypress.wall", (e) => {
      if (e.which === 13) {
        this.$modalBtnSend.click();
      }
    });


    // Modal close fallbacks
    $(document).on("click.wall", "#uploadModal [data-dismiss=\"modal\"], #uploadModal [data-bs-dismiss=\"modal\"]", (e) => {
      e.preventDefault();
      this.hideUploadModal();
    });
    document.getElementById('uploadModal')?.addEventListener('shown.bs.modal', () => {
      this.initTribute();
    });

    document.getElementById('commentModal')?.addEventListener('shown.bs.modal', () => {
      this.initModalTribute();
    });
  };

  getUploadModalInstance = () => {
    const modalEl = document.getElementById("uploadModal");
    if (!modalEl || !window.bootstrap || !window.bootstrap.Modal) return null;
    return window.bootstrap.Modal.getOrCreateInstance(modalEl);
  };

  showUploadModal = () => {
    const modal = this.getUploadModalInstance();
    if (modal) {
      modal.show();
      return;
    }
    if ($.fn && $.fn.modal) {
      $("#uploadModal").modal("show");
    }
  };

  hideUploadModal = () => {
    const modal = this.getUploadModalInstance();
    if (modal) {
      modal.hide();
      return;
    }
    if ($.fn && $.fn.modal) {
      $("#uploadModal").modal("hide");
    }
  };

  // ─── Interaction Logic ───────────────────────────────────────────────────

  handleLikeToggle = ($btn, postId) => {
    if (!this.canUpload) {
      window.location.href = baseURL + "customer/login";
      return;
    }

    // Optimistic UI
    const isLiked = $btn.hasClass("active");
    const $count = $btn.find(".like-count");
    let count = parseInt($count.text()) || 0;

    $btn.toggleClass("active");
    $btn.find("i").toggleClass("fas far");
    $count.text(isLiked ? Math.max(0, count - 1) : count + 1);

    $.ajax({
      type: "POST",
      url: baseURL + "api/Wall/toggleLike",
      headers: { Authorization: X_API_KEY },
      data: { post_id: postId },
      error: () => {
        // Rollback on error
        $btn.toggleClass("active");
        $btn.find("i").toggleClass("fas far");
        $count.text(count);
      }
    });
  };

  handleCommentModalOpen = ($btn) => {
    this.activePostId = $btn.data("id");
    
    // Set modal content from button data
    $("#modal-post-img").attr("src", $btn.data("img"));
    $("#modal-user-avatar").attr("src", $btn.data("avatar"));
    $("#modal-username").text($btn.data("user"));
    $("#modal-vendor").html('<i class="fas fa-map-marker-alt mr-1"></i>' + $btn.data("vendor"));
    
    // Clear previous state
    this.$modalCommentList.html("");
    this.$modalCommentInput.val("");
    
    // Initialize Tribute for Modal if not already done
    this.initModalTribute();

    // Show Modal
    const modalEl = document.getElementById("commentModal");
    if (modalEl && window.bootstrap && window.bootstrap.Modal) {
      const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
      modal.show();
    }

    // Load comments
    this.loadComments(this.activePostId, this.$modalCommentList);
  };

  loadComments = (postId, $container) => {
    $container.html('<div class="text-center py-4"><i class="fas fa-spinner fa-spin fa-2x text-primary"></i></div>');
    
    $.ajax({
      type: "GET",
      url: baseURL + "api/Wall/getComments",
      headers: { Authorization: X_API_KEY },
      data: { post_id: postId },
      success: (res) => {
        if (res && res.data && res.data.length > 0) {
          let html = "";
          res.data.forEach(c => {
            html += `
              <div class="comment-item">
                <span class="comment-user">${c.Customer_Name || "User"}</span>
                <span class="comment-text">${formatMentions(formatCaptionUrls(c.Comment_Text), this.vendorsData)}</span>
              </div>
            `;
          });
          $container.html(html);
          // Scroll to bottom
          $container.scrollTop($container[0].scrollHeight);
        } else {
          $container.html('<div class="text-muted small text-center py-4">No comments yet. Be the first to reply!</div>');
        }
      }
    });
  };

  handleCommentSubmit = ($btn, postId, text, $input) => {
    if (!this.canUpload) {
      window.location.href = baseURL + "customer/login";
      return;
    }

    $btn.prop("disabled", true).html('<i class="fas fa-spinner fa-spin"></i>');

    $.ajax({
      type: "POST",
      url: baseURL + "api/Wall/addComment",
      headers: { Authorization: X_API_KEY },
      data: { post_id: postId, comment_text: text },
      success: () => {
        $input.val("");
        this.loadComments(postId, this.$modalCommentList);
        // Update main comment count in feed
        const $mainBtn = $(`.comment-toggle-btn[data-id="${postId}"] .comment-count`);
        const count = parseInt($mainBtn.text()) || 0;
        $mainBtn.text(count + 1);
      },
      complete: () => {
        $btn.prop("disabled", false).html('<i class="fas fa-paper-plane"></i>');
      }
    });
  };

  // ─── Image preview ────────────────────────────────────────────────────────

  handleImagePreview = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      this.$imagePreview.attr("src", ev.target.result).show();
      $(".preview-placeholder").hide();
      this.$previewTrigger.css("border", "none");
      
      // Add a 'Change' button if it doesn't exist
      if ($("#btn-change-image").length === 0) {
        $('<button type="button" id="btn-change-image" class="btn btn-sm btn-dark" style="position: absolute; top: 10px; right: 10px; opacity: 0.8; z-index: 10;">Change</button>')
          .appendTo(this.$previewTrigger)
          .on("click", (ev) => {
            ev.stopPropagation();
            this.resetPreview();
          });
      } else {
        $("#btn-change-image").show();
      }
    };
    reader.readAsDataURL(file);
  };

  // ─── Upload submit ────────────────────────────────────────────────────────

  handleUploadSubmit = (e) => {
    e.preventDefault();

    if (this.isSubmitting) {
      return;
    }

    if (!this.$imageInput[0].files.length) {
      alert("Please select a photo to upload");
      return;
    }

    const formData = new FormData(this.$uploadForm[0]);
    
    // Fallback: If vendor_id wasn't set by tribute-item-selected event, 
    // try to find the first mention in the caption and set it.
    if (!formData.get("vendor_id")) {
      const caption = (formData.get("caption") || "").toLowerCase();
      // Sort vendors by length descending to match longest name first (e.g. "Frozen World" before "Frozen")
      const sortedVendors = [...this.vendorsData].sort((a, b) => b.Vendor_Name.length - a.Vendor_Name.length);
      
      for (const v of sortedVendors) {
        const regularMention = "@" + v.Vendor_Name.toLowerCase();
        const collapsedMention = "@" + v.Vendor_Name.replace(/\s+/g, "").toLowerCase();
        
        if (caption.includes(regularMention) || caption.includes(collapsedMention)) {
          formData.set("vendor_id", v.Vendor_ID);
          this.$vendorIdInput.val(v.Vendor_ID);
          break;
        }
      }
    }

    if (!formData.get("vendor_id")) {
      alert("Please mention at least one vendor using @ in your caption");
      return;
    }

    this.isSubmitting = true;
    this.$submitBtn
      .prop("disabled", true)
      .html('<i class="fas fa-spinner fa-spin mr-2"></i>Posting...');

    $.ajax({
      type: "POST",
      url: baseURL + "api/Wall/uploadPost",
      headers: { Authorization: X_API_KEY },
      data: formData,
      processData: false,
      contentType: false,
      success: (res) => {
        this.hideUploadModal();
        this.$uploadForm[0].reset();
        this.resetPreview();
        this.loadWallPosts();
      },
      error: (xhr) => {
        const msg = (xhr.responseJSON && xhr.responseJSON.Msg) || "Failed to upload post";
        alert(msg);
      },
      complete: () => {
        this.isSubmitting = false;
        this.$submitBtn.prop("disabled", false).html("Post to Wall");
        // Reset feed to show the new post at the top
        this.offset = 0;
        this.hasMore = true;
        this.loadWallPosts(false);
      },
    });
  };

  // ─── Load feed ────────────────────────────────────────────────────────────

  loadWallPosts = (isNextPage = false) => {
    if (this.isLoading || (!isNextPage && !this.hasMore && this.offset > 0)) return;
    if (isNextPage && !this.hasMore) return;

    this.isLoading = true;

    if (!isNextPage) {
      this.offset = 0;
      this.hasMore = true;
      const skeletonHtml = `
          ${Array(4).fill(`
          <div class="wall-post mb-4" style="border-radius: 12px; overflow: hidden; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #f0f0f0;">
              <div class="post-header" style="padding: 12px 14px; display: flex; align-items: center;">
                  <div class="wall-skeleton-pulse" style="width: 38px; height: 38px; border-radius: 50%; margin-right: 12px;"></div>
                  <div style="flex-grow: 1;">
                      <div class="wall-skeleton-pulse" style="width: 120px; height: 14px; border-radius: 4px; margin-bottom: 8px;"></div>
                      <div class="wall-skeleton-pulse" style="width: 80px; height: 10px; border-radius: 4px;"></div>
                  </div>
              </div>
              <div class="wall-skeleton-pulse" style="width: 100%; aspect-ratio: 1/1; min-height: 250px;"></div>
              <div class="post-interaction-bar">
                  <div class="wall-skeleton-pulse" style="width: 60px; height: 18px; border-radius: 4px;"></div>
                  <div class="wall-skeleton-pulse" style="width: 60px; height: 18px; border-radius: 4px;"></div>
              </div>
              <div class="post-body" style="padding: 12px 14px;">
                  <div class="wall-skeleton-pulse" style="width: 85%; height: 12px; border-radius: 4px; margin-bottom: 8px;"></div>
                  <div class="wall-skeleton-pulse" style="width: 50px; height: 10px; border-radius: 4px;"></div>
              </div>
          </div>
          `).join("")}
      `;
      this.$wallFeed.html(skeletonHtml);
    } else {
      this.$loadMoreIndicator.fadeIn(200);
    }

    $.ajax({
      type: "GET",
      url: baseURL + "api/Wall/posts",
      headers: { Authorization: X_API_KEY },
      data: {
        limit: this.limit,
        offset: this.offset
      },
      success: (res) => {
        const posts = (res && res.data) || [];
        
        if (!isNextPage) {
          this.$wallFeed.empty();
        }

        if (posts.length > 0) {
          this.renderPosts(posts, isNextPage);
          this.offset += posts.length;
          
          // If we got fewer posts than requested, there are no more
          if (posts.length < this.limit) {
            this.hasMore = false;
          }
        } else {
          if (!isNextPage) {
            this.renderEmptyState();
          }
          this.hasMore = false;
        }
      },
      error: () => {
        if (!isNextPage) {
          this.$wallFeed.html(`
            <div class="alert alert-danger text-center">
              <i class="fas fa-exclamation-circle mr-2"></i>Failed to load posts.
            </div>
          `);
        }
      },
      complete: () => {
        this.isLoading = false;
        this.$loadMoreIndicator.fadeOut(200);
      }
    });
  };

  initInfiniteScroll = () => {
    if (!this.$loadMoreIndicator.length) return;

    const options = {
      root: null, // use browser viewport
      rootMargin: "0px 0px 200px 0px", // Trigger 200px before reaching the bottom
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMore && !this.isLoading) {
          this.loadWallPosts(true);
        }
      });
    }, options);

    observer.observe(this.$loadMoreIndicator[0]);
  };

  renderEmptyState = () => {
    const ctaHtml = this.canUpload
      ? `<button type="button" class="btn btn-upload-post mt-3" data-bs-toggle="modal" data-bs-target="#uploadModal">
                   <i class="fas fa-camera-retro mr-2"></i>Share First Photo
               </button>`
      : `<a href="${baseURL}customer/login" class="btn btn-upload-post mt-3">
                   <i class="fas fa-sign-in-alt mr-2"></i>Login to Post
               </a>`;

    this.$wallFeed.html(`
            <div class="wall-empty-state">
                <i class="fas fa-camera-retro fa-3x mb-3"></i>
                <h5>No posts yet</h5>
                <p>Be the first to share a photo!</p>
                ${ctaHtml}
            </div>
        `);
  };

  // ─── Render posts ─────────────────────────────────────────────────────────

  renderPosts = (posts, append = false) => {
    if (!append) {
      this.$wallFeed.empty();
    }

    posts.forEach((post) => {
      const userAvatar = post.Customer_Image_URL
        ? getImageUrl(post.Customer_Image_URL)
        : baseURL + "assets/dist/images/default-avatar.png";

      const postImageUrl = getImageUrl(post.Image_URL);
      const timeAgo = formatTimeAgo(post.Created_At);
      const vendorPath = post.Vendor_URL
        ? `${baseURL}customer/productListing/${encodeURIComponent(post.Vendor_URL)}`
        : "#";

      const isLiked = parseInt(post.Is_Liked) === 1;

      const postHtml = `
                <div class="wall-post">
                    <div class="post-header">
                        <img src="${userAvatar}"
                             alt="User"
                             class="post-avatar"
                             onerror="this.src='${baseURL}assets/dist/images/default-avatar.png'">
                        <div class="post-user-info">
                            <h3 class="post-username">${post.Customer_Name || "Locaso User"}</h3>
                            <a href="${vendorPath}" class="post-vendor-tag">
                                <i class="fas fa-map-marker-alt mr-1"></i>${post.Vendor_Name || "Vendor"}
                            </a>
                        </div>
                    </div>
                    <div class="post-image-container">
                        <img src="${postImageUrl}" alt="Wall Post" class="post-image" loading="lazy">
                    </div>
                    
                    <div class="post-interaction-bar">
                        <button class="interaction-btn like-btn ${isLiked ? 'active' : ''}" data-id="${post.Wall_Post_ID}">
                            <i class="${isLiked ? 'fas' : 'far'} fa-heart"></i>
                            <span class="like-count">${post.Like_Count || 0}</span>
                        </button>
                        <button class="interaction-btn comment-toggle-btn" 
                                data-id="${post.Wall_Post_ID}"
                                data-img="${postImageUrl}"
                                data-user="${post.Customer_Name || "Locaso User"}"
                                data-avatar="${userAvatar}"
                                data-vendor="${post.Vendor_Name || "Vendor"}">
                            <i class="far fa-comment"></i>
                            <span class="comment-count">${post.Comment_Count || 0}</span>
                        </button>
                    </div>


                    <div class="post-body">
                        ${post.Caption ? `
                            <p class="post-caption">
                                <strong>${post.Customer_Name || "User"}</strong>
                                ${formatMentions(formatCaptionUrls(post.Caption), this.vendorsData)}
                            </p>
                        ` : ''}
                        <div class="post-time">${timeAgo}</div>
                    </div>
                </div>
            `;

      this.$wallFeed.append(postHtml);
    });
  };

  // ─── Load vendors for dropdown ────────────────────────────────────────────

  loadVendors = (callback = null) => {
    $.ajax({
      type: "GET",
      url: baseURL + "api/Wall/getVendorsForWall",
      headers: { Authorization: X_API_KEY },
      success: (res) => {
        if (res && res.data) {
          this.vendorsData = res.data;
        }
        if (callback) callback();
      },
      error: () => {
        if (callback) callback();
      }
    });
  };

  /**
   * Creates a dedicated Tribute instance for a specific container/input.
   * This is much more stable than one global instance.
   */
  createTributeInstance = (containerEl) => {
    return new Tribute({
      values: this.vendorsData.map(v => ({
        key: v.Vendor_Name,
        value: v.Vendor_ID,
        url: v.Vendor_URL,
        avatar: v.Image_URL ? getImageUrl(v.Image_URL) : baseURL + "assets/dist/images/default-avatar.png"
      })),
      selectTemplate: (item) => {
        if (typeof item === "undefined") return null;
        return "@" + item.original.key.replace(/\s+/g, "");
      },
      menuItemTemplate: (item) => `
        <div class="tribute-item">
          <img src="${item.original.avatar}" class="tribute-avatar" onerror="this.src='${baseURL}assets/dist/images/default-avatar.png'">
          <span class="tribute-name">${item.string}</span>
        </div>
      `,
      lookup: "key",
      fillAttr: "key",
      container: containerEl,
      positionMenu: true 
    });
  };

  initTribute = () => {
    // 1. Attach to main upload caption
    const captionContainer = document.querySelector(".caption-anchor");
    const captionEl = captionContainer?.querySelector("textarea");
    
    if (captionEl && !captionEl.dataset.tributeAttached) {
      const tributeInstance = this.createTributeInstance(captionContainer);
      tributeInstance.attach(captionEl);
      captionEl.dataset.tributeAttached = "true";
      
      captionEl.addEventListener("tribute-item-selected", (e) => {
        if (e.detail && e.detail.item && e.detail.item.original) {
          this.$vendorIdInput.val(e.detail.item.original.value);
        }
      });
    }
  };

  initModalTribute = () => {
    const modalInput = document.getElementById("modal-comment-input");
    const modalAnchor = document.querySelector("#commentModal .modal-anchor");
    
    if (modalInput && modalAnchor && !modalInput.dataset.tributeAttached) {
      const tributeInstance = this.createTributeInstance(modalAnchor);
      tributeInstance.attach(modalInput);
      modalInput.dataset.tributeAttached = "true";
    }
  };

  // ─── Reset image preview ──────────────────────────────────────────────────

  resetPreview = () => {
    this.$imagePreview.attr("src", "#").hide();
    $(".preview-placeholder").show();
    this.$previewTrigger.css("border", "2px dashed #ddd");
    this.$imageInput.val("");
    $("#btn-change-image").hide();
  };
}
